import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ServicePageShell from "../components/ServicePageShell";
import ServiceInquiryForm from "../components/ServiceInquiryForm";
import {
  absoluteUrl,
  createBreadcrumbList,
  createFaqPageSchema,
  createHowToSchema,
  createWebPageSchema,
} from "../utils/seo";
import contactHero from "../assets/img/contacthero.png";
import siteData from "../data/siteData";

const pageTitle = "Salon Booking Software & Spa Management App | Lumina";
const pageDescription =
  "Lumina is salon booking software for spas, beauty salons, nail studios, and wellness teams with online appointments, staff, payments, and reports.";
const pageImage = "/images/lumina/hero-section.jpeg";

const platformStats = [
  ["24/7", "Online booking"],
  ["4.9", "Average salon rating"],
  ["50K+", "Bookings managed"],
  ["98%", "Happy customers"],
];

const features = [
  {
    image: "appointment-booking.png",
    title: "Appointment Booking",
    body: "Help salon owners save time and money by automating tasks like appointment scheduling, online booking, and more.",
  },
  {
    image: "services-packages.png",
    title: "Services & Packages",
    body: "Create service bundles to boost sales and client retention. Build memberships and subscription plans for recurring clients.",
  },
  {
    image: "staff-management.png",
    title: "Staff Management",
    body: "Easily manage staff members, schedules, performance, and commissions. Keep your workforce organized.",
  },
  {
    image: "client-management.png",
    title: "Client Management",
    body: "Manage clients with an intuitive interface, making it easy for users to pick up the software and get along quickly.",
  },
  {
    image: "multi-branch.png",
    title: "Multi-Branch Support",
    body: "Monitor all locations from one place, so you do not need to worry about the hassle of managing all branches separately.",
  },
  {
    image: "payments.png",
    title: "Payments - Online & Offline",
    body: "Securely pay and get service with ease. Let users handle transactions with multiple payment options.",
  },
  {
    image: "mobile-app.png",
    title: "Mobile App For Customers",
    body: "A sleek, user-friendly mobile experience for salons and spas, compatible with all devices.",
  },
  {
    image: "finances.png",
    title: "Finances (Expenses, Taxes)",
    body: "Get tools for a glimpse into your business growth, revenue, taxes, and financial trends.",
  },
  {
    image: "reporting.png",
    title: "Reporting",
    body: "Make informed decisions from graphs and data. Understand your business in depth with performance analytics.",
  },
];

const workflowSteps = [
  {
    icon: "event_available",
    title: "Book Appointment",
    body: "Users can self schedule, reschedule, cancel, book again, and manage group appointments.",
  },
  {
    icon: "location_on",
    title: "Visit Salon For Service",
    body: "Offer service to the customer. Provide customized experiences by knowing everything about them.",
  },
  {
    icon: "arrow_circle_right",
    title: "Customer Check-Out",
    body: "Quick and easy check-out options with auto-fill features and multiple payment options.",
  },
  {
    icon: "star",
    title: "Customer Gives Review",
    body: "Get reviews from satisfied customers after a booking to turn them into brand advocates.",
  },
];

const useCases = [
  { image: "comb.png", label: "Hair Salon" },
  { image: "nail.png", label: "Nail Salon" },
  { image: "lip.png", label: "Beauty Salon" },
  { image: "sun.png", label: "Tanning Salon" },
  { image: "arm.png", label: "Tattoo Studio" },
  { image: "treatment.png", label: "Cosmetology Services" },
  { image: "makup.png", label: "Make-up Studio" },
  { image: "bride.png", label: "Bridal Salon" },
  { image: "spa.png", label: "Spa Salon" },
  { image: "mobile.png", label: "Mobile Salon" },
];

const productCards = [
  {
    eyebrow: "Website",
    title: "Website for Your Brand",
    image: "website-showcase.jpeg",
    alt: "Lumina website and mobile experience presented in a modern office",
    className: "md:col-span-1",
  },
  {
    eyebrow: "Mobile App",
    title: "Booking App for Customers",
    image: "mobile-app.png",
    alt: "Lumina mobile booking app screens",
    className: "md:col-span-1",
  },
  {
    eyebrow: "Admin Panel",
    title: "Admin Panel for Professionals",
    image: "admin-panel-dashboard.png",
    alt: "Lumina admin panel dashboard overview",
    className: "md:col-span-2",
    imageAlign: "bottom-right",
    imageClassName: "h-auto w-full max-h-[280px] rounded-[20px] object-cover object-top shadow-none transition duration-500 group-hover:scale-[1.02] md:h-full md:max-h-[340px] md:-mb-[50px] md:-mr-[50px]",
  },
];

const dashboardScreens = [
  {
    title: "Daily bookings dashboard",
    text: "Track bookings, staff workload, payments, and customer activity from one calm admin view.",
    image: "website-admin-desktop.png",
    alt: "Lumina website and admin dashboard shown on a desktop screen",
  },
  {
    title: "Multi-device admin access",
    text: "Manage salon operations from desktop, tablet, or mobile without losing context.",
    image: "admin-panel-devices.png",
    alt: "Lumina admin panel displayed across desktop and mobile devices",
  },
];

const comparisonRows = [
  ["Online booking", "Built in", "Limited or add-on", "Mostly manual"],
  ["Staff and commission tracking", "Included", "Often basic", "Manual sheets"],
  ["Customer app", "Included", "Separate setup", "Not available"],
  ["Source code ownership", "Available", "No", "No"],
  ["Salon-ready workflows", "Yes", "Generic setup", "No automation"],
];

const pricingPlans = [
  {
    name: "Starter Demo",
    price: "Free",
    description: "A guided Lumina walkthrough for salon owners exploring online booking.",
    features: ["20-minute product tour", "Booking flow review", "Fit check for your business"],
    cta: "Book Free Demo",
  },
  {
    name: "Self-Hosted License",
    price: "Custom",
    description: "Own the platform source code and customize it for your salon brand.",
    features: ["Website, app, and admin panel", "Full source code access", "Brand and workflow customization"],
    cta: "Discuss Pricing",
    featured: true,
  },
  {
    name: "Growth Setup",
    price: "Custom",
    description: "Launch Lumina with help on setup, deployment, and business-specific modules.",
    features: ["Deployment support", "Custom modules", "Training and launch support"],
    cta: "Plan My Launch",
  },
];

const solutionHighlights = [
  {
    eyebrow: "Full source code included",
    title: "100% Self-Hosted Solution",
    body: "With an all-in-one inclusive platform that comes with full source code included, you have complete control over your app. Tailor it to your unique business needs and unlock the full potential of customization.",
    image: "solutions-self-hosted.png",
    alt: "Lumina mobile app source code shown on a desktop monitor",
  },
  {
    eyebrow: "Most affordable solution",
    title: "Save $1000s in Development Cost.",
    body: "Why spend countless hours and resources building an online booking platform from scratch when Lumina gives you the foundation you need? Invest wisely and maximize returns with a ready-to-shape platform.",
    image: "hero-section.jpeg",
    alt: "Lumina platform presentation in a modern office meeting room",
    imageFirst: false,
  },
  {
    eyebrow: "Regular updates",
    title: "Get All Future Updates",
    body: "When you invest in Lumina, you gain access to future improvements and capabilities built to support changing business needs.",
    imageSrc: contactHero,
    alt: "Lumina support specialist reviewing updates on a tablet",
  },
];

const testimonials = [
  {
    quote:
      "Lumina made our booking process calmer. Staff can see appointments clearly and customers book without waiting.",
    name: "Priya Mehta",
    role: "Owner, Velvet & Co.",
    rating: "5.0",
  },
  {
    quote:
      "The admin dashboard gives us bookings, payments, and staff work in one view. It saves time every day.",
    name: "Rahul Desai",
    role: "Manager, Studio Noir",
    rating: "4.9",
  },
  {
    quote:
      "Clients love discovering services and stylists before booking. It feels premium and easy.",
    name: "Sneha Shah",
    role: "Founder, Blossom Beauty",
    rating: "5.0",
  },
  {
    quote:
      "We wanted salon software that looked modern and handled real salon operations. Lumina fits both.",
    name: "Aarav Shah",
    role: "Salon Partner",
    rating: "4.8",
  },
  {
    quote:
      "The mobile flow is simple. Customers can book, check status, and come back for repeat appointments.",
    name: "Nisha Patel",
    role: "Spa Owner",
    rating: "5.0",
  },
  {
    quote:
      "Everything feels connected now: services, customers, staff, and payments. That is the biggest win.",
    name: "Meera Joshi",
    role: "Operations Lead",
    rating: "4.9",
  },
];

const faqs = [
  {
    question: "Is Lumina only for salons?",
    answer:
      "Lumina is designed for salons, spas, beauty studios, and appointment-led wellness businesses.",
  },
  {
    question: "Can customers book from mobile?",
    answer:
      "Yes. Lumina supports a mobile-first customer booking flow with service selection, stylist details, and booking status.",
  },
  {
    question: "Can salon owners manage staff?",
    answer:
      "Yes. Owners can manage services, staff availability, bookings, customers, and operational reports.",
  },
  {
    question: "Can this be customized for my brand?",
    answer:
      "Yes. Lumina can be adapted with salon branding, service categories, images, and custom operational modules.",
  },
  {
    question: "Does Lumina support online appointment booking?",
    answer:
      "Yes. Customers can book appointments online, choose services, view availability, and return for repeat bookings from the web or mobile experience.",
  },
  {
    question: "Is Lumina useful for multi-location salon businesses?",
    answer:
      "Yes. Lumina supports multi-branch operations so owners can manage bookings, staff, customers, payments, and performance reports across locations.",
  },
  {
    question: "Can Qbits9 help launch Lumina for my salon?",
    answer:
      "Yes. Qbits9 can help with setup, deployment, branding, custom modules, training, and launch support for salon and spa teams.",
  },
];

const seoAudienceLinks = [
  {
    title: "Salon booking software",
    text: "Accept online appointments, reduce calls, and keep repeat clients coming back through a clean booking flow.",
  },
  {
    title: "Spa management software",
    text: "Manage treatments, staff availability, customer history, payments, and branch-level reporting from one dashboard.",
  },
  {
    title: "Beauty salon CRM",
    text: "Store client preferences, service history, memberships, and follow-up context for more personal service.",
  },
  {
    title: "Self-hosted salon app",
    text: "Own the source code, customize your workflow, and launch a branded platform for web, admin, and mobile.",
  },
];

const relatedGrowthLinks = [
  {
    to: "/services/saas-erp-development",
    title: "Custom SaaS & ERP Development",
    text: "Extend Lumina or build connected dashboards for operations, branches, inventory, and finance.",
  },
  {
    to: "/services/web-mobile-development",
    title: "Web & Mobile App Development",
    text: "Launch customer-facing booking websites, apps, and responsive product experiences around Lumina.",
  },
  {
    to: "/services/ai-automation",
    title: "AI Automation Services",
    text: "Automate reminders, follow-ups, review requests, lead capture, and internal workflows.",
  },
];

const quickDemoSourceOptions = [
  "Lumina demo form",
  "WhatsApp",
  "Referral",
  "Search",
  "Social media",
  "Other",
];

const ArrowIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4 10.0156H11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M16.3094 9.46674C16.6624 9.747 16.6624 10.283 16.3094 10.5632L12.1352 13.8769C11.6765 14.2411 11 13.9144 11 13.3287V6.70131C11 6.11558 11.6765 5.78888 12.1352 6.15306L16.3094 9.46674Z" fill="currentColor" />
  </svg>
);

const SectionIntro = ({ eyebrow, title, text, className = "" }) => (
  <div className={`mx-auto max-w-3xl text-center ${className}`}>
    {eyebrow ? (
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
        {eyebrow}
      </p>
    ) : null}
    <h2 className="mt-3 text-[28px] font-bold leading-tight text-blue sm:text-3xl md:text-4xl font-unbounded">
      {title}
    </h2>
    {text ? (
      <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
        {text}
      </p>
    ) : null}
  </div>
);

const heroImage = process.env.PUBLIC_URL + "/images/lumina/hero-section.jpeg";

const HeroShowcase = () => (
  <div className="mx-auto w-full rounded-[10px] max-w-[520px] lg:max-w-[600px]">
    <img
      src={heroImage}
      alt="Lumina salon website and mobile app mockup"
      className="h-auto w-full object-contain rounded-[10px]"
      loading="eager"
      decoding="async"
      fetchPriority="high"
    />
  </div>
);

const FeatureCard = ({ feature }) => (
  <article className="bg-white p-5 sm:p-6">
    <img
      src={`${process.env.PUBLIC_URL}/images/lumina/features/${feature.image}`}
      alt=""
      className="h-10 w-10 object-contain"
      loading="lazy"
      decoding="async"
    />
    <h3 className="mt-5 text-base font-bold text-slate-900 font-unbounded">{feature.title}</h3>
    <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">{feature.body}</p>
  </article>
);

const WorkflowStepCard = ({ step, index }) => (
  <article className="relative text-center">
    <div className="flex items-center justify-center gap-4 lg:gap-8">
      <p className="text-sm font-bold uppercase tracking-[0.08em] text-slate-950 md:text-base">
        Step {index + 1}
      </p>
      {index < workflowSteps.length - 1 ? (
        <span className="hidden h-px w-20 border-t border-dotted border-slate-500 lg:block xl:w-32">
          <span className="sr-only">Next step</span>
        </span>
      ) : null}
    </div>
    <span className="mx-auto mt-8 flex h-14 w-14 items-center justify-center text-blue md:mt-12 md:h-16 md:w-16">
      {step.icon === "event_available" ? (
        <svg className="h-16 w-16" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <rect x="14" y="16" width="36" height="36" rx="6" stroke="currentColor" strokeWidth="3" />
          <path d="M22 10v12M42 10v12M14 27h36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M30 34h5v12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : step.icon === "location_on" ? (
        <svg className="h-16 w-16" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M32 9c-9 0-16 7-16 16 0 12 16 27 16 27s16-15 16-27c0-9-7-16-16-16z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
          <circle cx="32" cy="25" r="4" stroke="currentColor" strokeWidth="3" />
          <path d="M20 47c-6 2-10 5-10 8 0 5 10 8 22 8s22-3 22-8c0-3-4-6-10-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : step.icon === "arrow_circle_right" ? (
        <svg className="h-16 w-16" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M12 32h28M30 20l12 12-12 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M32 10a22 22 0 1 1-18 35" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : (
        <svg className="h-16 w-16" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <path d="M32 8l7 15 16 2-12 12 3 17-14-8-14 8 3-17L9 25l16-2 7-15z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
        </svg>
      )}
    </span>
    <h3 className="mt-6 text-lg font-bold text-slate-950 md:mt-8 md:text-xl font-unbounded">
      {step.title}
    </h3>
    <p className="mx-auto mt-4 max-w-xs text-sm leading-7 text-slate-600 md:mt-5 md:text-base md:leading-8 font-poppins">
      {step.body}
    </p>
  </article>
);

const Lumina = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const schemas = useMemo(
    () => [
      createWebPageSchema({
        name: pageTitle,
        description: pageDescription,
        path: "/lumina",
        imageUrl: pageImage,
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "Lumina", path: "/lumina" },
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Lumina",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        url: absoluteUrl("/lumina"),
        image: absoluteUrl(pageImage),
        description: pageDescription,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          category: "Free demo",
          availability: "https://schema.org/InStock",
          url: absoluteUrl("/lumina"),
        },
        provider: {
          "@type": "Organization",
          name: "QBITS9",
          url: absoluteUrl("/"),
        },
        featureList: features.map((feature) => feature.title),
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Lumina Salon Booking & Management Platform",
        brand: {
          "@type": "Brand",
          name: "QBITS9",
        },
        image: absoluteUrl(pageImage),
        description: pageDescription,
        category: "Salon management software",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          category: "Free demo",
          availability: "https://schema.org/InStock",
          url: absoluteUrl("/contact"),
        },
      },
      createFaqPageSchema({
        faqs,
        path: "/lumina",
        name: "Lumina salon booking software FAQs",
        description: pageDescription,
      }),
      createHowToSchema({
        name: "How salons use Lumina to manage appointments",
        description:
          "A simple flow for customers to book, staff to deliver the service, and owners to track salon performance.",
        path: "/lumina",
        steps: workflowSteps.map((step) => ({
          title: step.title,
          description: step.body,
        })),
      }),
      createBreadcrumbList([
        { name: "Home", path: "/" },
        { name: "Lumina", path: "/lumina" },
      ]),
    ],
    [],
  );

  return (
    <ServicePageShell
      title={pageTitle}
      description={pageDescription}
      canonicalPath="/lumina"
      image={pageImage}
      imageAlt="Lumina salon booking software dashboard and mobile app"
      keywords="salon booking software, salon management software, spa management software, beauty salon software, appointment booking app, self-hosted salon software, nail salon booking software"
      schemas={schemas}
    >
      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:py-14 md:min-h-[calc(100vh-88px)] md:grid-cols-[1.15fr_0.85fr] md:items-center lg:px-16">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary md:text-sm">
              Lumina Salon Management
            </p>
            <h1 className="mt-5 max-w-3xl text-[32px] font-bold leading-[1.14] text-blue sm:text-[42px] lg:text-[50px] font-unbounded">
              Salon booking software for growing salons, spas, and beauty studios
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
              Book appointments, manage staff, and enhance client experience with Lumina - a self-hosted platform for salons, spas, and wellness service businesses.
            </p>
            <div className="mt-8 grid gap-3 sm:flex sm:flex-wrap sm:gap-4">
              <Link
                to="/contact"
                className="group relative flex w-full items-center justify-center overflow-hidden rounded-full bg-primary px-6 py-3 text-sm sm:w-fit sm:min-w-[170px] md:px-8 md:py-4 md:text-base"
              >
                <span className="relative z-10 flex items-center gap-3 text-white font-unbounded">
                  Book Free Demo
                  <ArrowIcon />
                </span>
                <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary transition-all duration-700 group-hover:h-[15rem] group-hover:w-[15rem]" />
              </Link>
              <Link
                to="/contact"
                className="group relative flex w-full items-center justify-center overflow-hidden rounded-full bg-blue px-6 py-3 text-sm sm:w-fit sm:min-w-[170px] md:px-8 md:py-4 md:text-base"
              >
                <span className="relative z-10 flex items-center gap-3 text-white font-unbounded">
                  View Admin Panel
                  <ArrowIcon />
                </span>
                <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary transition-all duration-700 group-hover:h-[15rem] group-hover:w-[15rem]" />
              </Link>
              <a
                href={`https://wa.me/${siteData.phoneHref.replace(/\D/g, '')}?text=Hi%20Qbits9%2C%20I%20want%20a%20Lumina%20demo`}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex w-full items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-white px-6 py-3 text-sm text-primary sm:w-fit sm:min-w-[170px] md:px-8 md:py-4 md:text-base"
              >
                <span className="relative z-10 flex items-center gap-3 font-unbounded">
                  WhatsApp Demo
                  <ArrowIcon />
                </span>
              </a>
            </div>
            <div className="mt-8 rounded-[8px] border border-primary/15 bg-[#FFF7EF] p-4 sm:max-w-2xl">
              <div className="grid gap-3 sm:grid-cols-3">
                {platformStats.slice(0, 3).map(([value, label]) => (
                  <div key={label}>
                    <p className="text-2xl font-bold text-primary font-unbounded">{value}</p>
                    <p className="mt-1 text-xs font-semibold text-blue">{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 max-w-2xl text-sm font-semibold leading-7 text-slate-600 md:text-base">
              Get a guided walkthrough with our product expert -{" "}
              <Link to="/contact" className="text-blue underline decoration-primary underline-offset-4 transition hover:text-primary">
                Book a quick call.
              </Link>
            </p>
          </div>

          <div className="relative min-w-0">
            <HeroShowcase />
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 lg:grid-cols-[0.82fr_1.18fr] lg:px-16">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Built To Grow
            </p>
            <h2 className="mt-4 text-[28px] font-bold leading-tight text-blue sm:text-3xl md:text-4xl font-unbounded">
              Rank for the services clients already search before they book.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-600 md:text-base font-poppins">
              Lumina helps salons turn search traffic into appointments with a branded booking website, customer app, service pages, staff profiles, reviews, and a fast demo-ready experience.
            </p>
            <Link
              to="/contact"
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-blue px-6 py-3 text-sm font-bold text-white transition hover:bg-primary sm:w-auto font-unbounded"
            >
              Plan growth setup
              <ArrowIcon />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {seoAudienceLinks.map((item) => (
              <article key={item.title} className="rounded-[8px] border border-slate-200 bg-[#FFF7EF] p-5">
                <h3 className="text-base font-bold text-blue font-unbounded">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5EF] py-14 md:py-16">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-16">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Quick Demo
            </p>
            <h2 className="mt-4 text-[28px] font-bold leading-tight text-blue sm:text-3xl md:text-4xl font-unbounded">
              See Lumina before your next salon rush starts.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
              Share your details and we will walk you through bookings, staff management, dashboards, and customer app flow.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {["No pressure walkthrough", "Salon-specific suggestions", "Fast WhatsApp follow-up", "Pricing clarity"].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-[8px] bg-white px-4 py-3 text-sm font-semibold text-blue">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] text-white">OK</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <ServiceInquiryForm
            title="Book your free Lumina demo"
            description="Tell us a little about your salon or spa. We will reply with the best demo slot."
            submitLabel="Book Free Demo"
            serviceName="Lumina Demo"
            sourceOptions={quickDemoSourceOptions}
            defaultSource="Lumina demo form"
            successTitle="Your Lumina demo request is in."
            successDescription="Our team will contact you shortly with the next available demo slot."
          />
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            Use Cases
          </p>
          <h2 className="mx-auto mt-4 max-w-5xl text-[30px] font-bold leading-tight text-blue sm:text-4xl md:text-5xl font-unbounded">
            A perfect partner for businesses in beauty & wellness industry
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
            Lumina empowers salon owners, spa managers, and wellness professionals to streamline day-to-day operations, enhance customer experience, and drive business growth.
          </p>

          <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-x-4 gap-y-10 sm:mt-12 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-5">
            {useCases.map((item) => (
              <div key={item.label} className="flex flex-col items-center">
                <span
                  className="block h-16 w-16 bg-orange-500"
                  style={{
                    WebkitMask: `url(${process.env.PUBLIC_URL}/images/lumina/use-cases/${item.image}) center / contain no-repeat`,
                    mask: `url(${process.env.PUBLIC_URL}/images/lumina/use-cases/${item.image}) center / contain no-repeat`,
                  }}
                  aria-hidden="true"
                />
                <p className="mt-5 text-sm font-medium text-slate-700 md:text-base font-poppins">
                  {item.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center">
            <Link
              to="/contact"
              className="group relative inline-flex w-full max-w-[520px] items-center justify-center gap-3 overflow-hidden rounded-full bg-primary px-5 py-4 text-center text-sm font-bold text-white sm:w-auto sm:px-7 md:min-w-[420px] font-unbounded"
            >
              <span className="relative z-10 min-w-0">
                Still wondering if Lumina suits your business?
              </span>
              <span className="relative z-10 flex items-center justify-center rounded-full transition group-hover:translate-x-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M4 10.0156H11" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"></path>
                  <path d="M16.3094 9.46674C16.6624 9.747 16.6624 10.283 16.3094 10.5632L12.1352 13.8769C11.6765 14.2411 11 13.9144 11 13.3287L11 6.70131C11 6.11558 11.6765 5.78888 12.1352 6.15306L16.3094 9.46674Z" fill="#fff"></path>
                </svg>
              </span>
              <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary transition-all duration-700 group-hover:h-[25rem] group-hover:w-[50rem]" />
            </Link>
            <p className="mt-5 text-sm font-medium text-slate-600 md:text-base font-poppins">
              See how Lumina fits your exact business model - it only takes 20 minutes.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Products
            </p>
            <h2 className="mx-auto mt-4 max-w-3xl text-[30px] font-bold leading-tight text-blue sm:text-4xl md:text-5xl font-unbounded">
              Simplify, manage, succeed with Lumina complete solution
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
              With our powerful admin panel, customer mobile app, and digital presence tools, you can manage everything while clients enjoy a clean, accessible experience.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:mt-12 md:grid-cols-2">
            {productCards.map((card) => (
              <article
                key={card.title}
                className={`group overflow-hidden rounded-[8px] bg-[#FFF7EF] ${card.className}`}
              >
                <div className={`grid gap-6 p-5 sm:p-6 md:grid-cols-[auto_minmax(0,1fr)] md:p-8 ${card.className.includes("md:col-span-2") ? "md:min-h-[360px]" : "md:min-h-[320px]"}`}>
                  <div className="relative z-10 flex flex-col items-start justify-center text-left">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                      {card.eyebrow}
                    </p>
                    <h3 className="mt-4 max-w-[260px] text-xl font-bold leading-tight text-blue sm:text-2xl font-unbounded">
                      {card.title}
                    </h3>
                    <Link
                      to="/contact"
                      className="group/btn relative mt-6 inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-primary px-5 py-3 text-xs font-bold text-white sm:mt-7 font-unbounded"
                    >
                      <span className="relative z-10">View Demo</span>
                      <span className="relative z-10 flex transition group-hover/btn:translate-x-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M4 10.0156H11" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"></path>
                          <path d="M16.3094 9.46674C16.6624 9.747 16.6624 10.283 16.3094 10.5632L12.1352 13.8769C11.6765 14.2411 11 13.9144 11 13.3287L11 6.70131C11 6.11558 11.6765 5.78888 12.1352 6.15306L16.3094 9.46674Z" fill="#fff"></path>
                        </svg>
                      </span>
                      <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary transition-all duration-700 group-hover/btn:h-[15rem] group-hover/btn:w-[15rem]" />
                    </Link>
                  </div>
                  <div className={`flex min-w-0 items-end ${card.imageAlign === "bottom-right" ? "justify-center self-end md:justify-end" : "justify-center"}`}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/lumina/products/${card.image}`}
                      alt={card.alt}
                      className={card.imageClassName || "h-auto max-h-[260px] w-full max-w-[360px] rounded-[20px] object-contain object-center shadow-none transition duration-500 group-hover:scale-[1.02] md:h-full md:max-h-[300px] md:w-auto"}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Product Demo"
            title="Watch the 60-second Lumina product flow."
            text="A short walkthrough of how a customer books, how your team manages appointments, and how owners monitor salon activity."
          />
          <div className="mx-auto mt-10 max-w-5xl overflow-hidden rounded-[8px] bg-blue shadow-[0_24px_70px_rgba(15,23,42,0.16)]">
            <div className="relative aspect-video">
              <img
                src={`${process.env.PUBLIC_URL}/images/lumina/products/website-admin-desktop.png`}
                alt="Lumina product demo preview showing website and admin screens"
                className="h-full w-full object-cover opacity-80"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-slate-950/20 px-5 text-center">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-6 py-4 text-sm font-bold text-primary shadow-lg transition hover:bg-primary hover:text-white sm:px-8 font-unbounded"
                >
                  Book live video demo
                  <ArrowIcon />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5EF] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Dashboard"
            title="Real dashboard screens for real salon work."
            text="Owners need clear operational screens, not a pile of disconnected tools. Lumina keeps the important work visible."
          />
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {dashboardScreens.map((screen) => (
              <article key={screen.title} className="overflow-hidden rounded-[8px] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <div className="p-5 sm:p-6">
                  <h3 className="text-xl font-bold text-blue font-unbounded">{screen.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">{screen.text}</p>
                </div>
                <div className="h-[260px] overflow-hidden bg-[#FFF7EF] md:h-[500px]">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/lumina/products/${screen.image}`}
                    alt={screen.alt}
                    className="h-full w-full max-h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="lumina-features" className="scroll-mt-28 bg-[#F8F5EF] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
              Features
            </p>
            <h2 className="mx-auto mt-4 max-w-3xl text-[30px] font-bold leading-tight text-slate-950 sm:text-4xl md:text-5xl font-unbounded">
              Discover work-boosting features at your fingertips!
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
              Lumina comes with tools to boost revenue, manage your calendar, and retain customers, so your salon can stay focused while the system handles the busy work.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:mt-12 lg:grid-cols-3 lg:gap-6">
            {features.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-xl text-center text-sm text-slate-600 font-poppins">
            A live demo helps you focus on the features that matter most.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Growth Stack"
            title="Connect Lumina with the Qbits9 growth services around it."
            text="A product page can bring the lead in, but growth comes from the surrounding website, app, automation, and reporting system."
          />
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {relatedGrowthLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group rounded-[8px] border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_18px_50px_rgba(15,23,42,0.07)]"
              >
                <h3 className="text-lg font-bold text-blue font-unbounded">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">{item.text}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary transition group-hover:translate-x-1">
                  View service
                  <ArrowIcon className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="mx-auto max-w-5xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              Workflow
            </p>
            <h2 className="mx-auto mt-4 max-w-4xl text-[30px] font-bold leading-tight text-blue sm:text-4xl md:text-5xl font-unbounded">
              Get a glimpse of our butter-smooth workflow. Efficiency at its finest!
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 md:mt-6 font-poppins">
              Lumina salon software helps you delight customers beyond their in-shop experience.
            </p>
          </div>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4 lg:gap-12">
            {workflowSteps.map((step, index) => (
              <WorkflowStepCard key={step.title} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-[1fr_0.9fr] lg:px-16">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary md:text-sm">
              Increase Sales
            </p>
            <h2 className="mt-5 max-w-2xl text-[30px] font-bold leading-tight text-blue sm:text-4xl md:text-5xl font-unbounded">
              Get more bookings, more business.
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 md:mt-7 md:text-lg font-poppins">
              By offering an easy-to-use online platform, you attract more customers who prefer the convenience of booking online. Lumina provides a user-friendly booking system that allows customers to book your services or appointments conveniently.
            </p>
            <Link
              to="/contact"
              className="group relative mt-8 inline-flex w-full items-center justify-center gap-3 overflow-hidden rounded-full bg-primary px-7 py-4 text-sm font-bold text-white sm:w-auto md:mt-10 font-unbounded"
            >
              <span className="relative z-10">Learn More</span>
              <span className="relative z-10 flex transition group-hover:translate-x-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M4 10.0156H11" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"></path>
                  <path d="M16.3094 9.46674C16.6624 9.747 16.6624 10.283 16.3094 10.5632L12.1352 13.8769C11.6765 14.2411 11 13.9144 11 13.3287L11 6.70131C11 6.11558 11.6765 5.78888 12.1352 6.15306L16.3094 9.46674Z" fill="#fff"></path>
                </svg>
              </span>
              <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary transition-all duration-700 group-hover:h-[15rem] group-hover:w-[15rem]" />
            </Link>
          </div>
          <div className="relative min-w-0 overflow-hidden rounded-[8px] bg-[#FFF7EF] px-4 pt-8 md:px-8">
            <img
              src={`${process.env.PUBLIC_URL}/images/lumina/increase-sales.png`}
              alt="Lumina booking app shown with customer cards and statistics"
              className="mx-auto h-auto w-full max-w-[500px] object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl space-y-12 px-5 md:space-y-16 lg:px-16">
          {solutionHighlights.map((item) => (
            <div
              key={item.title}
              className="grid items-center gap-8 md:gap-10 lg:grid-cols-2"
            >
              <div className={`${item.imageFirst === false ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden rounded-[8px]">
                  <img
                    src={item.imageSrc || `${process.env.PUBLIC_URL}/images/lumina/${item.image}`}
                    alt={item.alt}
                    className="mx-auto h-auto w-full max-w-[460px] rounded-[8px] object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div className={`${item.imageFirst === false ? "lg:order-1" : ""}`}>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  {item.eyebrow}
                </p>
                <h2 className="mt-3 max-w-xl text-[30px] font-bold leading-tight text-blue sm:text-4xl md:text-5xl font-unbounded">
                  {item.title}
                </h2>
                <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 md:text-base font-poppins">
                  {item.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Reviews"
            title="Real people, real reviews, real results."
            text="A modern salon product should feel useful to the business and effortless for the customer."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial) => (
              <article key={testimonial.name} className="rounded-[8px] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.05)]">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-primary">*****</p>
                  <span className="rounded-full bg-[#FFF7EF] px-3 py-1 text-xs font-bold text-primary">
                    {testimonial.rating}
                  </span>
                </div>
                <p className="mt-5 text-sm leading-7 text-slate-600 font-poppins">"{testimonial.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                    {testimonial.name.split(" ").map((part) => part[0]).join("")}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-blue">{testimonial.name}</span>
                    <span className="block text-xs text-grey">{testimonial.role}</span>
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-5 text-center sm:grid-cols-2 lg:grid-cols-4">
            {platformStats.map(([value, label]) => (
              <div key={label} className="rounded-[8px] bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.05)]">
                <p className="text-4xl font-bold text-primary font-unbounded">{value}</p>
                <p className="mt-2 text-sm font-semibold text-blue">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-16">
          <SectionIntro eyebrow="Questions" title="Frequently asked." />
          <div className="mt-10 space-y-4">
            {faqs.map((faq, index) => (
              <article key={faq.question} className="rounded-[8px] border border-slate-200 bg-white p-5">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 text-left"
                  aria-expanded={openFaq === index}
                >
                  <span className="text-base font-bold text-blue font-unbounded">{faq.question}</span>
                  <span className={`text-2xl font-bold text-primary transition-transform duration-300 ${openFaq === index ? "rotate-45" : ""}`}>
                    +
                  </span>
                </button>
                <div className={`grid overflow-hidden transition-all duration-300 ${openFaq === index ? "mt-3 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0"}`}>
                  <div className="min-h-0">
                    <p className="text-sm leading-7 text-slate-600 font-poppins">{faq.answer}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F5EF] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Comparison"
            title="Why choose Lumina over generic tools?"
            text="Lumina is built around salon operations, so your team gets booking, customers, staff, and reporting in one product."
          />
          <div className="mt-10 overflow-x-auto rounded-[8px] border border-slate-200 bg-white">
            <table className="min-w-[760px] w-full text-left text-sm">
              <thead className="bg-blue text-white">
                <tr>
                  {["Capability", "Lumina", "Generic booking app", "Manual process"].map((heading) => (
                    <th key={heading} className="px-5 py-4 font-bold font-unbounded">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row[0]} className="border-t border-slate-200">
                    {row.map((cell, index) => (
                      <td key={cell} className={`px-5 py-4 ${index === 0 ? "font-bold text-blue" : "text-slate-600"}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <SectionIntro
            eyebrow="Pricing"
            title="Start with a free demo, then choose the right setup."
            text="Every salon runs a little differently. We keep the first step simple and shape pricing around your launch needs."
          />
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {pricingPlans.map((plan) => (
              <article
                key={plan.name}
                className={`rounded-[8px] border p-6 ${plan.featured ? "border-primary bg-[#FFF7EF] shadow-[0_18px_50px_rgba(255,107,0,0.12)]" : "border-slate-200 bg-white"}`}
              >
                <h3 className="text-xl font-bold text-blue font-unbounded">{plan.name}</h3>
                <p className="mt-4 text-4xl font-bold text-primary font-unbounded">{plan.price}</p>
                <p className="mt-4 text-sm leading-7 text-slate-600 font-poppins">{plan.description}</p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-sm font-medium text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[9px] text-white">OK</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition font-unbounded ${plan.featured ? "bg-primary text-white hover:bg-blue" : "bg-blue text-white hover:bg-primary"}`}
                >
                  {plan.cta}
                  <ArrowIcon />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary py-16 text-center text-white md:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">Try Lumina</p>
          <h2 className="mt-3 text-[30px] font-bold leading-tight sm:text-4xl md:text-5xl font-unbounded">
            We will walk you through how Lumina solves daily salon headaches.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/85 sm:text-base sm:leading-8 font-poppins">
            Book a quick demo and see how online booking, staff management, payments, and reports can work together for your salon.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex w-full justify-center rounded-full bg-white px-6 py-3 text-sm font-bold text-primary transition hover:bg-blue hover:text-white sm:w-auto">
              Schedule Demo
            </Link>
            <Link to="/contact" className="inline-flex w-full justify-center rounded-full border border-white/40 px-6 py-3 text-sm font-bold text-white transition hover:bg-white hover:text-primary sm:w-auto">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </ServicePageShell>
  );
};

export default Lumina;
